/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputProps,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { getCustomerName } from '../../lib/customer.mjs'
import { fetchEstate, findManyEstates } from '../../lib/rest/estateAPIClient'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { getEstateCreateLink, getEstateLink } from '../../lib/links/estateLinks'
import { ActionIcon, Box, Text } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import LabelledValue from '../LabelledValue'
import Amount from '../transaction/Amount'

export function getEstateOptionLabel (estate: Estate) {
  return `${estate.reference} - ${estate.title}`
}

export type PropValProps = {
  label: ReactNode,
  value?: ReactNode
}

function EstateOption (props: SearchInputRenderOption<Estate>) {
  const { option } = props
  const { item } = option
  const intl = useIntl()
  return (
    <Box w="100%">
      <Text inherit>
        {getEstateOptionLabel(item)}
      </Text>
      <Box fz="sm" c="dimmed">
        <LabelledValue
          label={intl.formatMessage({
            id: 'price_excluding_tax',
            defaultMessage: 'Prix HT'
          })}
          value={item.price != null
            ? <Amount value={item.price} inherit withCurrency />
            : null}
        />

        {item.City != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'city',
              defaultMessage: 'Ville'
            })}
            value={item.City?.name}
          />
          : null}

        {item.Owner != null
          ? <LabelledValue
            label={intl.formatMessage({
              id: 'owner',
              defaultMessage: 'Propriétaire'
            })}
            value={getCustomerName(item.Owner)}
          />
          : null}
      </Box>
    </Box>
  )
}

export type EstateInputProps = Omit<SearchInputProps<Estate>, 'data' | 'onSearchChange'> & {
  queryFilter?: Record<string, any>,
}

function EstateInput (props: EstateInputProps) {
  const intl = useIntl()
  const {
    disabled,
    id,
    label,
    name = 'estateId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['estates', queryFilter, searchParams],
    queryFn: () => findManyEstates({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.estates : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<Estate>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getEstateOptionLabel(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const estateQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['estate', value],
    queryFn: () => fetchEstate(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: estate } = estateQuery

  useEffect(() => {
    if (estate != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getEstateOptionLabel(estate)
      }))
    }
  }, [estate, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'estate',
        defaultMessage: 'Bien'
      })}
      {...others}
      data={options}
      disabled={disabled}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={EstateOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
      rightSection={<>
        {value == null
          ? <ActionIcon
            component={AnchorLink}
            target="_blank"
            title={intl.formatMessage({
              id: 'new',
              defaultMessage: 'Nouveau'
            })}
            to={getEstateCreateLink()}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faPlusCircle}
            />
          </ActionIcon>
          : <ActionIcon
            disabled={!value}
            component={AnchorLink}
            target="_blank"
            to={getEstateLink(Number(value))}
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faLink}
            />
          </ActionIcon>
        }
      </>}
    />
  )
}

export default EstateInput
